<section class="claim-overview">
  <div class="claim-overview__summary">
    <div class="claim-overview__summary-copy">
      <p class="claim-overview__label">{{ 'claim.outstandingTotal' | t }}</p>
      <p class="claim-overview__amount">{{ formatCurrency(claim.outstandingAmount) }}</p>
    </div>
    <button type="button" class="claim-overview__pay-button" data-track="quick_payment" (click)="triggerPayNow()">
      {{ 'claim.payNow' | t }}
    </button>
  </div>

  @if (claim.activeInstallmentPlan; as plan) {
    <div class="claim-overview__installment-plan">
      <span class="case-badge case-badge--positive">{{ 'claim.activeInstallmentPlan' | t }}</span>
      @if (plan.openInstallments > 0 || plan.installments.length > 0) {
        <button
          type="button"
          class="claim-overview__details-button"
          (click)="toggleInstallments()"
          [attr.aria-expanded]="showInstallments()">
          {{ (showInstallments() ? 'claim.hideInstallments' : 'claim.showInstallments') | t }}
        </button>
      }
      @if (showInstallments()) {
        <div class="claim-overview__installments">
          @if (plan.openInstallments > 0) {
            <div class="claim-overview__row">
              <span>{{ 'widget.installments' | t }}</span>
              <span>{{ plan.openInstallments }}</span>
            </div>
          }
          @for (rate of plan.installments; track $index) {
            <div class="claim-overview__row">
              <span>{{ rate.labelKey | t: (rate.labelParams ?? {}) }}</span>
              <span>
                @if (rate.date) { {{ formatDate(rate.date) }} }
                @if (rate.amount > 0) { <strong>{{ formatCurrency(rate.amount) }}</strong> }
              </span>
            </div>
          }
        </div>
      }
    </div>
  }

  <div class="claim-overview__breakdown">
    <div class="claim-overview__breakdown-heading">{{ 'claim.breakdown' | t }}</div>
    <div class="claim-overview__breakdown-content" [attr.aria-label]="'claim.summaryLabel' | t">
        <div class="claim-overview__row claim-overview__row--main">
          <button
            type="button"
            class="claim-overview__main-claim-toggle"
            (click)="toggleMainClaimDetails()"
            [attr.aria-expanded]="showMainClaimDetails()"
            [attr.aria-label]="mainClaimLabel()">
            @if (showMainClaimDetails()) { - } @else { + }
          </button>
          <span>{{ mainClaimLabel() }}</span>
          <span>{{ formatCurrency(claim.breakdown.mainClaim) }}</span>
        </div>

        @if (showMainClaimDetails()) {
          <div class="claim-overview__invoices">
            @if (claim.breakdown.titleAware && claim.breakdown.titleDetails.length > 0) {
              <p class="claim-overview__subheading">{{ 'claim.titleDetails' | t }}</p>
              @for (title of claim.breakdown.titleDetails; track $index) {
                <article class="claim-overview__invoice">
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.titleDesignation' | t }}</span>
                    <strong>{{ title.designation || ('claim.notAvailable' | t) }}</strong>
                  </div>
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.titleNumber' | t }}</span>
                    <strong>{{ title.number || ('claim.notAvailable' | t) }}</strong>
                  </div>
                  @if (title.court) {
                    <div class="claim-overview__invoice-line">
                      <span>{{ 'claim.titleCourt' | t }}</span>
                      <strong>{{ title.court }}</strong>
                    </div>
                  }
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.date' | t }}</span>
                    <strong>{{ formatDate(title.date) }}</strong>
                  </div>
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.mainClaim' | t }}</span>
                    <strong>{{ formatCurrency(title.mainClaim) }}</strong>
                  </div>
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.titleIncludedCosts' | t }}</span>
                    <strong>{{ formatCurrency(title.costs) }}</strong>
                  </div>
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.titleIncludedInterest' | t }}</span>
                    <strong>{{ formatCurrency(title.interest) }}</strong>
                  </div>
                </article>
              }
            } @else if (claim.breakdown.invoices.length === 0) {
              <p class="claim-overview__empty">{{ 'claim.noInvoices' | t }}</p>
            } @else {
              <p class="claim-overview__subheading">{{ invoicePresentationText('sectionLabel') }}</p>
              @if (claim.breakdown.invoicePresentation.note) {
                <p class="claim-overview__note">{{ invoicePresentationText('note') }}</p>
              }
              @for (invoice of claim.breakdown.invoices; track invoice.number) {
                <article class="claim-overview__invoice">
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.invoice' | t }}</span>
                    <strong>{{ invoice.number }}</strong>
                  </div>
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.date' | t }}</span>
                    <strong>{{ formatDate(invoice.date) }}</strong>
                  </div>
                  @if (claim.breakdown.invoicePresentation.amountMode === 'open') {
                    <div class="claim-overview__invoice-line">
                      <span>{{ invoicePresentationText('amountLabel') }}</span>
                      <strong>{{ formatCurrency(invoice.amount) }}</strong>
                    </div>
                  }
                  <div class="claim-overview__invoice-line">
                    <span>{{ 'claim.reason' | t }}</span>
                    <strong>{{ invoice.reason }}</strong>
                  </div>
                </article>
              }
            }
          </div>
        }

        <div class="claim-overview__row">
          <span>{{ 'claim.interest' | t }}</span>
          <span>{{ formatCurrency(claim.breakdown.interest) }}</span>
        </div>
        <div class="claim-overview__row claim-overview__row--separator">
          <span>{{ 'claim.costs' | t }}</span>
          <span>{{ formatCurrency(claim.breakdown.costs) }}</span>
        </div>
        <div class="claim-overview__row claim-overview__row--total">
          <span>{{ 'claim.totalClaim' | t }}</span>
          <span>{{ formatCurrency(totalClaim) }}</span>
        </div>
    </div>
  </div>

  <div class="claim-overview__details-entry">
    <button
      #detailsTrigger
      type="button"
      class="claim-overview__details-button"
      (click)="openDetails()"
      [attr.aria-expanded]="detailsOpen()"
      aria-haspopup="dialog">
      {{ 'claim.showDetails' | t }}
    </button>
  </div>
</section>

@if (detailsOpen()) {
  <div class="modal-backdrop claim-details-modal">
    <section
      #detailsDialog
      class="modal-panel claim-details-modal__panel"
      role="dialog"
      tabindex="-1"
      aria-modal="true"
      aria-labelledby="claim-details-title">
      <header class="modal-header">
        <h2 id="claim-details-title">{{ 'claim.detailsTitle' | t }}</h2>
        <button
          #detailsCloseButton
          type="button"
          class="modal-close-btn"
          (click)="closeDetails()"
          [attr.aria-label]="'common.close' | t">×</button>
      </header>

      <div class="claim-details-modal__hero">
        <div class="claim-details-modal__hero-total">
          <span>{{ 'claim.detailsOutstandingTotal' | t }}</span>
          <strong>{{ formatCurrency(claim.outstandingAmount) }}</strong>
        </div>
        <dl class="claim-details-modal__hero-meta">
          <div>
            <dt>{{ 'dashboard.aktenzeichen' | t }}</dt>
            <dd>{{ claim.aktenzeichen }}</dd>
          </div>
          <div>
            <dt>{{ 'dashboard.creditor' | t }}</dt>
            <dd>{{ claim.creditorName }}</dd>
          </div>
        </dl>
      </div>

      <div class="claim-details-modal__tabs" role="tablist" [attr.aria-label]="'claim.detailsTitle' | t">
        @for (tab of detailTabs(); track tab) {
          <button
            type="button"
            class="claim-details-modal__tab"
            role="tab"
            [class.is-active]="activeDetailsTab() === tab"
            [attr.aria-selected]="activeDetailsTab() === tab"
            [attr.aria-controls]="'claim-details-panel-' + tab"
            [id]="'claim-details-tab-' + tab"
            (click)="activateDetailsTab(tab)">
            {{ detailTabLabel(tab) }}
          </button>
        }
      </div>

      <div class="modal-body claim-details-modal__body">
        @switch (activeDetailsTab()) {
          @case ('overview') {
            <section
              class="claim-details-modal__panel-content"
              role="tabpanel"
              id="claim-details-panel-overview"
              aria-labelledby="claim-details-tab-overview">
              <section class="claim-details-modal__section" aria-labelledby="claim-breakdown-title">
                <div class="claim-details-modal__section-header">
                  <h3 id="claim-breakdown-title">{{ 'claim.breakdown' | t }}</h3>
                </div>
                <dl class="claim-details-modal__ledger">
                  <div>
                    <dt>{{ mainClaimLabel() }}</dt>
                    <dd>{{ formatCurrency(claim.breakdown.mainClaim) }}</dd>
                  </div>
                  <div>
                    <dt>{{ 'claim.interest' | t }}</dt>
                    <dd>{{ formatCurrency(claim.breakdown.interest) }}</dd>
                  </div>
                  <div>
                    <dt>{{ 'claim.costs' | t }}</dt>
                    <dd>{{ formatCurrency(claim.breakdown.costs) }}</dd>
                  </div>
                  <div class="claim-details-modal__ledger-total">
                    <dt>{{ 'claim.detailsOutstandingTotal' | t }}</dt>
                    <dd>{{ formatCurrency(claim.outstandingAmount) }}</dd>
                  </div>
                </dl>
              </section>

              @if (claim.breakdown.detailedStatement.length > 0) {
                <section class="claim-details-modal__section" aria-labelledby="claim-detailed-statement-title">
                  <div class="claim-details-modal__section-header">
                    <h3 id="claim-detailed-statement-title">{{ 'claim.detailedStatement' | t }}</h3>
                    <p>{{ 'claim.detailedStatementCount' | t: { count: claim.breakdown.detailedStatement.length } }}</p>
                  </div>
                  <div class="claim-details-modal__table-wrap">
                    <table class="claim-details-modal__table" [class.claim-details-modal__table--detailed]="hasDetailedClaimColumns()">
                      <thead>
                        @if (hasDetailedClaimColumns()) {
                          <tr>
                            <th scope="col">{{ 'claim.date' | t }}</th>
                            <th scope="col">{{ 'documentUploadModal.comment' | t }}</th>
                            <th scope="col">Haupt-Forderung</th>
                            <th scope="col">{{ 'claim.interest' | t }}</th>
                            <th scope="col">{{ 'p1.nonInterestCosts' | t }}</th>
                            <th scope="col">{{ 'p1.interestCosts' | t }}</th>
                            <th scope="col">{{ 'caseView.claimAmount' | t }}</th>
                            <th scope="col">{{ 'caseView.breakdown.payment' | t }}</th>
                          </tr>
                        } @else {
                          <tr>
                            <th scope="col">{{ 'claim.description' | t }}</th>
                            <th scope="col">{{ 'claim.date' | t }}</th>
                            <th scope="col">{{ 'claim.amount' | t }}</th>
                          </tr>
                        }
                      </thead>
                      <tbody>
                        @for (row of claim.breakdown.detailedStatement; track $index) {
                          @if (hasDetailedClaimColumns()) {
                            <tr>
                              <td>{{ detailedDate(row.date) }}</td>
                              <td>{{ detailedText(row.description) }}</td>
                              <td>{{ detailedValue(row.mainClaim, row.mainClaimText) }}</td>
                              <td>{{ detailedValue(row.interest, row.interestText) }}</td>
                              <td>{{ detailedValue(row.nonInterestCosts, row.nonInterestCostsText) }}</td>
                              <td>{{ detailedValue(row.interestCosts, row.interestCostsText) }}</td>
                              <td>{{ detailedValue(row.claimAmount, row.claimAmountText) }}</td>
                              <td>{{ detailedValue(row.payment, row.paymentText) }}</td>
                            </tr>
                          } @else {
                            <tr>
                              <td>{{ detailedText(row.description) }}</td>
                              <td>{{ detailedDate(row.date) }}</td>
                              <td>{{ detailedAmount(row) }}</td>
                            </tr>
                          }
                        }
                      </tbody>
                    </table>
                  </div>
                </section>
              }
            </section>
          }

          @case ('invoices') {
            <section
              class="claim-details-modal__panel-content"
              role="tabpanel"
              id="claim-details-panel-invoices"
              aria-labelledby="claim-details-tab-invoices">
              <div class="claim-details-modal__section-header">
                <h3>{{ invoicePresentationText('sectionLabel') }}</h3>
                @if (claim.breakdown.invoicePresentation.note) {
                  <p>{{ invoicePresentationText('note') }}</p>
                }
              </div>
              <div class="claim-details-modal__cards">
                @for (invoice of claim.breakdown.invoices; track invoice.number) {
                  <article class="claim-details-modal__record">
                    <header class="claim-details-modal__record-head">
                      <strong>{{ 'claim.invoice' | t }} {{ invoice.number }}</strong>
                      <span>{{ formatDate(invoice.date) }}</span>
                    </header>
                    @if (claim.breakdown.invoicePresentation.amountMode === 'open') {
                      <div class="claim-details-modal__line">
                        <span>{{ invoicePresentationText('amountLabel') }}</span>
                        <strong>{{ formatCurrency(invoice.amount) }}</strong>
                      </div>
                    }
                    <div class="claim-details-modal__line">
                      <span>{{ 'claim.reason' | t }}</span>
                      <strong>{{ invoice.reason }}</strong>
                    </div>
                  </article>
                } @empty {
                  <p class="claim-details-modal__empty">{{ 'claim.noInvoices' | t }}</p>
                }
              </div>
            </section>
          }

          @case ('titles') {
            <section
              class="claim-details-modal__panel-content"
              role="tabpanel"
              id="claim-details-panel-titles"
              aria-labelledby="claim-details-tab-titles">
              <div class="claim-details-modal__cards">
                @for (title of claim.breakdown.titleDetails; track $index) {
                  <article class="claim-details-modal__record">
                    <header class="claim-details-modal__record-head">
                      <strong>{{ title.designation || ('claim.notAvailable' | t) }}</strong>
                      <span>{{ formatDate(title.date) }}</span>
                    </header>
                    <div class="claim-details-modal__line">
                      <span>{{ 'claim.titleNumber' | t }}</span>
                      <strong>{{ title.number || ('claim.notAvailable' | t) }}</strong>
                    </div>
                    @if (title.court) {
                      <div class="claim-details-modal__line">
                        <span>{{ 'claim.titleCourt' | t }}</span>
                        <strong>{{ title.court }}</strong>
                      </div>
                    }
                    <div class="claim-details-modal__line">
                      <span>{{ 'claim.mainClaim' | t }}</span>
                      <strong>{{ formatCurrency(title.mainClaim) }}</strong>
                    </div>
                    <div class="claim-details-modal__line">
                      <span>{{ 'claim.titleIncludedCosts' | t }}</span>
                      <strong>{{ formatCurrency(title.costs) }}</strong>
                    </div>
                    <div class="claim-details-modal__line">
                      <span>{{ 'claim.titleIncludedInterest' | t }}</span>
                      <strong>{{ formatCurrency(title.interest) }}</strong>
                    </div>
                  </article>
                } @empty {
                  <p class="claim-details-modal__empty">{{ 'claim.noTitleDetails' | t }}</p>
                }
              </div>
            </section>
          }

          @case ('payments') {
            <section
              class="claim-details-modal__panel-content"
              role="tabpanel"
              id="claim-details-panel-payments"
              aria-labelledby="claim-details-tab-payments">
              <div class="claim-details-modal__table-wrap">
                <table class="claim-details-modal__table">
                  <thead>
                    <tr>
                      <th scope="col">{{ 'claim.date' | t }}</th>
                      <th scope="col">{{ 'claim.paymentReference' | t }}</th>
                      <th scope="col">{{ 'claim.amount' | t }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (payment of claim.breakdown.payments; track $index) {
                      <tr>
                        <td>{{ formatDate(payment.date) }}</td>
                        <td>{{ payment.reference || ('claim.notAvailable' | t) }}</td>
                        <td>{{ formatCurrency(payment.amount) }}</td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="3" class="claim-details-modal__empty-cell">{{ 'claim.noPayments' | t }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </section>
          }

        }
      </div>
    </section>
  </div>
}
